import { all, put, select, takeEvery } from "redux-saga/effects";
import * as Actions from './actions';
import { get, findIndex, cloneDeep, find } from 'lodash';
import { showToast } from '../notification';
import constants, { 
  ATTRIBUTE_ADMIN, 
  ATTRIBUTE_ALL_ACCESS, 
  ATTRIBUTE_MANAGER, 
  ATTRIBUTE_EDITOR 
} from '../../../../helpers/constant';

import {
  getOrgUsersApi,
  createTeamApi,
  updateTeamApi,
  deleteTeamApi,
  getTeamsApi,
  getUsersActivitiesApi
} from '../../../../helpers/userManagementApi';

import {
  REMOVE_ADVERTISER,
  ADD_ADVERTISER,
  updateAdvertiserStatus
} from '../advertiser';

import {
  ADD_CAMPAIGN,
  REMOVE_CAMPAIGN,
  UPDATE_CAMPAIGN
} from '../campaign';

import {namespace as userManagementNamespace, Status} from '../userManagement/actions'
import { modules } from 'veritone-redux-common';

import { getAdvertiser } from '../advertiser';
const {  
  user: { selectUser }
} = modules;

const errorToast = {
  message: "Something went wrong",
  action: "Dismiss",
  type: constants.FAILED
};

function* loadUserActivities() {
  
  const state = yield select();
  const config = state.config;

  const users = get(state, [userManagementNamespace, 'users', 'data'], []);
  const userActivitiesStatus = get(state, [userManagementNamespace, 'users', 'activitiesDataStatus']);
  
  const userIds = users.map(user => user.id);

  if (userIds.length === 0 || userActivitiesStatus === Status.STATUS_LOADING) {
    return;
  }

  yield put(Actions.setUserActivitiesStatus(Status.STATUS_LOADING));

  // fetch users data in chunks of 300
  const chunkSize = 300;
  const userActivities = { data: {} };
  while (userIds.length > 0) {
    const fetchIds = userIds.splice(0, chunkSize);
    const res = yield getUsersActivitiesApi(config.urlApi, fetchIds);
    userActivities.data = {...userActivities.data, ...res.data};
  }
  
  const updatedUsers = users.map((user) => {
    const id = user.id;
    const numCampaigns = get(userActivities, ['data', id, 'numCampaigns'], 0);
    const numAdvertisers = get(userActivities, ['data', id, 'numAdvertisers'], 0);
    
    return {
      ...user,
      numCampaigns: parseInt(numCampaigns),
      numAdvertisers: parseInt(numAdvertisers)
    };
  });

  yield put(Actions.setUserActivitiesStatus(Status.STATUS_LOADED));
  yield put(Actions.setOrgUser(updatedUsers));
}

function* loadOrgUsers() {
  const state = yield select();
  const config = state.config;
  const userState = state.user;
  const orgId = get(userState, 'user.organization.organizationId');
  // Note: graphQL call accept multiple org ids, we currently only need to fetch by one id
  const orgUsersStatus = get(state, [userManagementNamespace, 'users', 'status']);
  if (orgUsersStatus === Status.STATUS_LOADING){
    return;
  }
  yield put(Actions.setOrgUserStatus(Status.STATUS_LOADING));
  const organizationIds = orgId && [orgId] || [];
  const fetchResults = yield getOrgUsersApi(config.urlApi, organizationIds);

  if (fetchResults && fetchResults.data) {
    const clonedData = cloneDeep(fetchResults.data);

    const users = clonedData.reduce((accum, user) => {
      const { roles, id, lastLoginDateTime, imageUrl, status, firstName, lastName } = user;
      if (status == Actions.Status.ACTIVE) {
        user.lastActiveDate = lastLoginDateTime;
        user.profileImage = imageUrl;
        user.fullName = `${firstName} ${lastName}`;

        if (roles) {
          for (let index = 0; index < roles.length; index++) {
            const roleId = roles[index].id;
            if (roleId === ATTRIBUTE_ALL_ACCESS) {
              user.role = ATTRIBUTE_ALL_ACCESS;
              user.isAllAccess = true;
              user.isAdmin = true;
              user.isManager = true;
              break;
            } else if (roleId === ATTRIBUTE_ADMIN) {
              user.role = ATTRIBUTE_ADMIN;
              user.isAdmin = true;
              user.isManager = true;
              break;
            } else if (roleId === ATTRIBUTE_MANAGER) {
              user.role = ATTRIBUTE_MANAGER;
              user.isManager = true;
              break;
            } else if (roleId === ATTRIBUTE_EDITOR) {
              user.role = ATTRIBUTE_EDITOR;
              break;
            }
          }
          delete user.roles;
          user.role && accum.push(user);
        }
      }
      return accum;
    }, []);
    yield put(Actions.setOrgUser(users)); 
  } else {
    yield put(showToast({
      message: "Load User Error",
      action: "Dismiss",
      type: constants.FAILED
    }));
  }
};

function* loadTeams(action) {
  const state = yield select();
  const config = state.config;
  const userState = state.user;
  const orgId = get(userState, 'user.organization.organizationId');
  const teamsStatus = get(state, [userManagementNamespace, 'teams', 'status']);
  if (teamsStatus === Status.STATUS_LOADING){
    return;
  }
  yield put(Actions.setTeamsStatus(Status.STATUS_LOADING));
  const fetchResults = yield getTeamsApi(config.urlApi, action.payload.teamIds, orgId);
  if (fetchResults && fetchResults.code === Actions.FetchResult.FETCH_SUCCESS) {
    yield put(Actions.setTeams([...fetchResults.data]));
  } else {
    yield put(showToast({
      message: "Load Team Error",
      action: "Dismiss",
      type: constants.FAILED
    }));
  }
};

function* updateTeam(action) {
  const config = yield select(state => state.config);
  const { teamInfo } = action.payload;
  const fetchResults = yield updateTeamApi(config.urlApi, teamInfo);
  if (fetchResults && fetchResults.code === Actions.FetchResult.FETCH_SUCCESS) {
    const state = yield select(state => state[Actions.namespace]);
    const allTeams = [...get(state, 'teams.data', [])];
    Object.keys(fetchResults.data) && Object.keys(fetchResults.data).forEach(id => {
      const teamData = get(fetchResults, ['data', id], {});
      const currentTeamIndex = findIndex(allTeams, { id });
      if (currentTeamIndex > -1) {
        allTeams[currentTeamIndex] = {...allTeams[currentTeamIndex], ...teamData};
      }
    })
    yield put(Actions.setTeams(allTeams));
  } else {
    const errorToast = {
      message: 'Update team failed',
      action: "Dismiss",
      type: constants.FAILED
    };
    yield put(showToast(errorToast));
  }
};

function* deleteTeam(action) {
  const config = yield select(state => state.config);
  const { id } = action.payload;
  const state = yield select( state => state[Actions.namespace]);
  const allTeams = [...get(state, 'teams.data', [])];
  const currentTeamIndex = findIndex(allTeams, { id });
  if (currentTeamIndex > -1) {
    allTeams.splice(currentTeamIndex, 1);
    yield put(Actions.setTeams(allTeams)); 
  }
  const fetchResults = yield deleteTeamApi(config.urlApi, id);

  if (fetchResults && fetchResults.code !== Actions.FetchResult.FETCH_SUCCESS) {
    yield put(showToast(errorToast));
  }
};

function* createTeam(action) {
  const config = yield select(state => state.config);
  const { teamInfo } = action.payload;
  const fetchResults = yield createTeamApi(config.urlApi, teamInfo);
  if (fetchResults && fetchResults.code === Actions.FetchResult.FETCH_SUCCESS) {
    const state = yield select(state => state[Actions.namespace]);
    const allTeams = [...get(state, 'teams.data', [])];
    const { id } = fetchResults.data;
    const currentTeamIndex = findIndex(allTeams, { id });
    if (currentTeamIndex < 0) {
      allTeams.push(fetchResults.data);
      yield put(Actions.setTeams(allTeams));
    }
  } else {
    const errorToast = {
      message: 'Create team failed',
      action: "Dismiss",
      type: constants.FAILED
    };
    yield put(showToast(errorToast));
  }
};

function* updateUsersTeams(action) {
  const config = yield select(state => state.config);
  const state = yield select(state => state[Actions.namespace]);
  const teams = [...get(state, 'teams.data', [])];
  const clonedTeams = cloneDeep(teams);

  const { usersTeamsHashmap } = action.payload;
  const {
    allTeams,
    updatedTeams
  } = Object.keys(usersTeamsHashmap).reduce((accumulator, userId) => {
    
    const { teamIds: userTeams = [] } = usersTeamsHashmap[userId];
    const { isManager = false } = usersTeamsHashmap[userId].user;
    const { allTeams, updatedTeams } = accumulator;
    
    clonedTeams.forEach(team => {
      const { id: teamId, memberIds, managerIds } = team;
      allTeams[teamId] = team;

      const matchingPrevMemberIndex = memberIds.indexOf(userId);
      const matchingPrevManagerIndex = managerIds.indexOf(userId);

      const isPrevMembers = matchingPrevMemberIndex !== -1;
      const isPrevManagers = matchingPrevManagerIndex !== -1;
      const isATeamUser = userTeams.includes(teamId);

      if (isATeamUser) {
        //New Team User
        if (isManager && !isPrevManagers) {
          //Add to Manager list
          allTeams[teamId].managerIds.push(userId);
          updatedTeams[teamId] = allTeams[teamId];
        } else if (!isManager && !isPrevMembers ){
          //Add to Member List
          allTeams[teamId].memberIds.push(userId);
          updatedTeams[teamId] = allTeams[teamId];
        }

      } else {
        if (isPrevManagers) {
          //Remove from prev manager list
          allTeams[teamId].managerIds.splice(matchingPrevManagerIndex, 1);
          updatedTeams[teamId] = allTeams[teamId];
        } else if (isPrevMembers) {
          //Remove from prev manager list
          allTeams[teamId].memberIds.splice(matchingPrevMemberIndex, 1);
          updatedTeams[teamId] = allTeams[teamId];
        } else {
          //Keep everything the same
        }
      }
    });

    return accumulator;
  }, {
    allTeams: {},
    updatedTeams: {}
  });

  yield put(Actions.setTeams(Object.values(allTeams)));

  //call update api
  const fetchResults = yield updateTeamApi(config.urlApi, updatedTeams);

  if (fetchResults && fetchResults.code === Actions.FetchResult.FETCH_SUCCESS) {
    //TODO: update successful
  } else {
    //TODO: update error message        
  }
}

function* addAdvertiser(action){
  const advertiserId = get(action,'payload.advertiser.advertiser_id');
  // do not process decrease for advertiser with id = 'editing'
  if(advertiserId === 'editing'){
    return;
  }

  const currentUser = yield select(selectUser);
  const { userId: currentUserId } = currentUser;

  const users = yield select(state => state[userManagementNamespace].users);
  if( users.status === Status.STATUS_LOADED ){
    const updatedUsers = cloneDeep(users.data);
    if(currentUserId) {
      const user = find(updatedUsers, {id:currentUserId});
      if(user) {
        user.numAdvertisers = user.numAdvertisers + 1;
        yield put(Actions.setOrgUser(updatedUsers)); 
      }
    }
  }
}

function* removeAdvertiser(action){ 

  const { advertiser = {} } = action.payload;
  const { owner, advertiser_id: advertiserId, campaigns : removedAdvertiserCampaigns = [] } = advertiser;
  
  // do not process decrease for advertiser with id = 'editing'
  if(advertiserId === 'editing'|| !owner){
    return;
  }  

  const users = yield select(state => state[userManagementNamespace].users);

  if(users.status === Status.STATUS_LOADED){
    
    const updatedUsers = cloneDeep(users.data);
    const user = find(updatedUsers,{ id : owner });

    if( user ){      
      const minusCampNum = removedAdvertiserCampaigns.length;
      user.numCampaigns = user.numCampaigns > minusCampNum ? ( user.numCampaigns - minusCampNum ) : 0;
      user.numAdvertisers = user.numAdvertisers > 0 ? ( user.numAdvertisers - 1 ) : 0;
      yield put(Actions.setOrgUser(updatedUsers));  
    }
  }
}


function* removeCampaign(action){ 
  const { campaign = {}} = action.payload;
  const state = yield select();
  const getAdvertiserById = getAdvertiser(state);
  const { campaign_id : campaignId, advertiser_id : advertiserId } = campaign;

  const { owner } = yield select(state => getAdvertiser(state)(advertiserId));
  // do not process decrease for campaign with id = 'editing'
  if(campaignId === 'editing' || !owner){
    return;
  }

  const users = yield select(state => state[userManagementNamespace].users);
  if( users.status === Status.STATUS_LOADED ){
    const updatedUsers = cloneDeep(users.data);    
    const user = find(updatedUsers,{ id : owner });
      if(user){
        user.numCampaigns = user.numCampaigns > 0 ? user.numCampaigns - 1 : 0;
        yield put(Actions.setOrgUser(updatedUsers));  
      }
  }
  if (advertiserId && campaignId) {
    const advertiser = getAdvertiserById(advertiserId);
    advertiser.campaigns = (advertiser.campaigns || []).filter(
      item => item.campaign_id !== campaignId
    );
    yield put(updateAdvertiserStatus(advertiser));
  }
}

function* addCampaign(action){
  const campaignId = get(action,'payload.campaign.campaign_id');
  const advertiserId = get(action,'payload.campaign.advertiser_id');
  const { owner } = yield select(state => getAdvertiser(state)(advertiserId));
  const state = yield select();
  const getAdvertiserById = getAdvertiser(state);
  const advertiser = getAdvertiserById(advertiserId);
  // do not process increase for campaign with id = 'editing'
  if(campaignId === 'editing' || !owner){
    return;
  }    
  
  const users = yield select(state => state[userManagementNamespace].users);

  if( users.status === Status.STATUS_LOADED ){
    const updatedUsers = cloneDeep(users.data);
    const user = find(updatedUsers, { id : owner});
    if(user) {
      user.numCampaigns = user.numCampaigns + 1;
      yield put(Actions.setOrgUser(updatedUsers)); 
    }
  }
  yield put(updateAdvertiserStatus(advertiser));
}

function* updateCampaign(action){
  const advertiserId = get(action,'payload.campaign.advertiser_id');
  if(advertiserId){
  const state = yield select();
  const getAdvertiserById = getAdvertiser(state);
  const advertiser = getAdvertiserById(advertiserId);
  yield put(updateAdvertiserStatus(advertiser));
}
}

export default function* userManagement() {
  const ActionType = Actions.ActionType;

  yield all([
    takeEvery(ActionType.LOAD_USER_ACTIVITIES, loadUserActivities),
    takeEvery(ActionType.LOAD_ORG_USERS, loadOrgUsers),
    takeEvery(ActionType.LOAD_TEAMS, loadTeams),
    takeEvery(ActionType.UPDATE_TEAM, updateTeam),
    takeEvery(ActionType.DELETE_TEAM, deleteTeam),
    takeEvery(ActionType.CREATE_TEAM, createTeam),
    takeEvery(ActionType.UPDATE_USERS_TEAMS, updateUsersTeams),
    takeEvery(ADD_ADVERTISER, addAdvertiser),
    takeEvery(REMOVE_ADVERTISER, removeAdvertiser),
    takeEvery(ADD_CAMPAIGN, addCampaign),
    takeEvery(UPDATE_CAMPAIGN, updateCampaign),
    takeEvery(REMOVE_CAMPAIGN, removeCampaign),
  ]);
};
